/**
 * Запись персоны (§6.4, §71, §77).
 *
 * Отдельный immersive-экран вне общего шелла: во время записи на экране
 * только видоискатель, суфлёр и таймер. Ни навигации, ни бейджей (§71).
 * Оптимизируется прежде всего под смартфон (§77).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Teleprompter } from '../../features/capture/Teleprompter';
import { useRecorder } from '../../features/capture/useRecorder';
import { uploadFile } from '../../features/upload/uploadFile';
import { personaApi } from '../../api/persona';
import { captureDuration } from '@alterium/shared';
import { useTranslation } from 'react-i18next';

const formatTime = (seconds: number): string => {
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export function CapturePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const recorder = useRecorder();
  const script = useQuery({ queryKey: ['capture-script'], queryFn: personaApi.captureScript });

  const minSec = script.data?.duration.minSec ?? captureDuration.minSec;
  const targetSec = script.data?.duration.targetSec ?? captureDuration.targetSec;
  const maxSec = script.data?.duration.maxSec ?? captureDuration.maxSec;

  // Живой поток в видоискатель. Отдельный эффект: поток появляется асинхронно.
  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;

    const stream = recorder.stream.current;
    if (stream && recorder.state !== 'recorded') {
      element.srcObject = stream;
      return;
    }

    // Снимаем поток явно: `srcObject` сильнее `src`, и оставшийся привязанным
    // поток показывал бы камеру вместо записи.
    element.srcObject = null;
  }, [recorder.state, recorder.stream]);

  // Автостоп на верхней границе: длиннее — уже не полезно (§6.3).
  useEffect(() => {
    if (recorder.state === 'recording' && recorder.elapsedSec >= maxSec) recorder.stop();
  }, [recorder, recorder.state, recorder.elapsedSec, maxSec]);

  const submit = useMutation({
    mutationFn: async () => {
      const captured = recorder.captured;
      if (!captured) throw new Error(t('consent.noRecording'));

      const extension = captured.mimeType.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([captured.blob], `persona-capture.${extension}`, {
        type: captured.mimeType.split(';')[0] ?? 'video/webm',
      });

      const asset = await uploadFile(file, {
        kind: 'video',
        onProgress: ({ loaded, total }) => setUploadProgress(Math.round((loaded / total) * 100)),
      });

      await personaApi.submitCapture(asset.id, script.data?.script.version);
    },
    onSuccess: () => {
      recorder.release();
      navigate('/persona', { replace: true });
    },
    onSettled: () => setUploadProgress(null),
  });

  const tooShort = (recorder.captured?.durationSec ?? 0) < minSec;
  const progress = Math.min(1, recorder.elapsedSec / targetSec);

  return (
    <div className="flex min-h-full flex-col bg-page">
      {/* §71: во время записи на экране не остаётся ничего лишнего */}
      {recorder.state !== 'recording' ? (
        <header className="flex items-center justify-between px-5 py-4">
          <span className="text-card-title font-semibold tracking-tight">{t('capture.title')}</span>
          <button
            type="button"
            onClick={() => {
              recorder.release();
              navigate('/persona');
            }}
            aria-label={t('common.close')}
            className="rounded-control p-1.5 text-content-muted transition-colors duration-fast hover:text-content"
          >
            <X className="h-5 w-5" strokeWidth={1.75} />
          </button>
        </header>
      ) : null}

      {/*
        Суфлёр — вверху, у самой камеры (§6.4).

        Раньше текст жил под видоискателем, и на готовом ролике было видно,
        что человек читает вниз и вбок: фотоаватар оживляет один кадр записи,
        и взгляд из него никуда не девается. Теперь строка стоит там же, где
        объектив, и смотреть в неё — почти то же, что смотреть в камеру.
      */}
      {(recorder.state === 'ready' || recorder.state === 'recording') && script.data ? (
        <div className="shrink-0 pb-4">
          <Teleprompter
            script={script.data.script}
            progress={progress}
            active={recorder.state === 'recording'}
          />
        </div>
      ) : null}

      <div className="flex flex-1 flex-col gap-4 px-5 pb-8 lg:flex-row lg:gap-8 lg:px-8">
        <div className="relative mx-auto w-full max-w-[420px] overflow-hidden rounded-card bg-surface-secondary lg:mx-0">
          <div className="aspect-[9/16] w-full">
            {/*
              Разные `key` обязательны: без них React переиспользует один и тот
              же элемент, привязанный к живому потоку, и вместо записи снова
              показывается камера. Ровно это и происходило.
            */}
            {recorder.state === 'recorded' && recorder.captured ? (
              <video
                key="recorded"
                src={recorder.captured.url}
                controls
                playsInline
                className="h-full w-full object-cover"
              />
            ) : (
              <video
                key="live"
                ref={videoRef}
                autoPlay
                muted
                playsInline
                // Зеркалим: человек привык видеть себя в отражении.
                className="h-full w-full -scale-x-100 object-cover"
              />
            )}
          </div>

          {recorder.state === 'recording' ? (
            <div className="absolute left-1/2 top-4 flex -translate-x-1/2 items-center gap-2 rounded-control bg-page/80 px-3 py-1.5">
              <span className="h-2 w-2 rounded-full bg-error" />
              <span className="text-meta tabular-nums text-content">
                {formatTime(recorder.elapsedSec)}
              </span>
              <span className="text-meta text-content-muted">/ {formatTime(targetSec)}</span>
            </div>
          ) : null}

          {/* §69: прогресс текста — тонкая полоса, без процентов */}
          {recorder.state === 'recording' ? (
            <div className="absolute inset-x-0 bottom-0">
              <div className="progress-track rounded-none">
                <div className="progress-bar rounded-none" style={{ width: `${progress * 100}%` }} />
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col">
          {recorder.error ? <Alert tone="error">{recorder.error}</Alert> : null}
          {submit.isError ? <Alert tone="error">{(submit.error as Error).message}</Alert> : null}

          {/*
            Инструкция показывается и при ошибке доступа: иначе пользователь
            остаётся на экране без единой кнопки и не может повторить попытку
            (agents/web-persona-capture.md §4).
          */}
          {recorder.state === 'idle' || recorder.state === 'error' ? (
            <Instructions
              instructions={script.data?.instructions ?? []}
              minSec={minSec}
              maxSec={maxSec}
              retry={recorder.state === 'error'}
              onStart={() => void recorder.requestPermission()}
            />
          ) : null}

          {recorder.state === 'recorded' && recorder.captured ? (
            <div className="flex flex-col gap-4 pt-4">
              <div>
                <p className="text-card-title">{t('capture.ready')}</p>
                <p className="mt-1 text-body text-content-secondary">
                  {t('capture.duration', { value: formatTime(recorder.captured.durationSec) })}{' '}
                  {tooShort
                    ? t('capture.tooShort', { seconds: minSec })
                    : t('capture.reviewBeforeSend')}
                </p>
              </div>

              {uploadProgress !== null ? (
                <div className="progress-track">
                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
                </div>
              ) : null}

              <div className="flex flex-wrap gap-3">
                <Button
                  variant="primary"
                  onClick={() => submit.mutate()}
                  disabled={submit.isPending}
                >
                  {submit.isPending ? t('auth.sending') : t('capture.useVideo')}
                </Button>
                <Button onClick={recorder.reset} disabled={submit.isPending}>{t('persona.recapture')}</Button>
              </div>
            </div>
          ) : null}

          {recorder.state === 'ready' || recorder.state === 'recording' ? (
            <div className="flex justify-center pt-4">
              {recorder.state === 'ready' ? (
                <Button variant="primary" onClick={() => void recorder.start()}>{t('capture.start')}</Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={recorder.stop}
                  disabled={recorder.elapsedSec < 5}
                >{t('capture.stop')}</Button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** §6.1: инструкция перед записью. */
function Instructions({
  instructions,
  minSec,
  maxSec,
  retry,
  onStart,
}: {
  instructions: string[];
  minSec: number;
  maxSec: number;
  retry?: boolean;
  onStart: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-5 pt-2">
      <div>
        <h1 className="text-section-title font-semibold">{t('capture.heading')}</h1>
        <p className="mt-2 text-body text-content-secondary">
          {t('capture.explanation')}
        </p>
      </div>

      <ul className="flex flex-col gap-2">
        {instructions.map((item) => (
          <li key={item} className="flex gap-2.5 text-body text-content-secondary">
            <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-content-muted" />
            {item}
          </li>
        ))}
      </ul>

      <p className="text-meta text-content-muted">
        {t('capture.takesMinutes', {
          from: Math.round(minSec / 60),
          to: Math.round(maxSec / 60),
          count: Math.round(maxSec / 60),
        })}
      </p>

      <div>
        <Button variant="primary" onClick={onStart}>
          {retry ? t('common.tryAgain') : t('capture.enableCamera')}
        </Button>
      </div>

      {retry ? (
        <p className="text-meta text-content-muted">
          {t('capture.permissionHint')}
        </p>
      ) : null}
    </div>
  );
}
